import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, Shield, Brain, Plus, ArrowRight, Trash2, Copy, Pencil, Check, X, Archive, RotateCcw } from 'lucide-react';
import { analysesApi } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { ShareButton } from '@/pages/SharedView';
import { DealScoreCard, DealScoreRing } from '@/components/DealScore';

const fmt = (v) => {
  if (v === undefined || v === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

const StatusBadge = ({ status }) => {
  const config = {
    complete: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Complete' },
    in_progress: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', label: 'In Progress' },
    calculated: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-400', label: 'Calculated' },
    draft: { bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', text: 'text-zinc-400', label: 'Draft' },
  };
  const c = config[status] || config.draft;
  return (
    <Badge variant="outline" className={`${c.bg} ${c.border} ${c.text} text-[10px]`}>
      {c.label}
    </Badge>
  );
};

export default function DashboardPage({ results, analysis, analyses, onSelect, onNew, onNavigate, onRefresh }) {
  const dc = results?.deal_comparison || {};
  const dcf = results?.dcf || {};
  const flags = results?.trend_flags || [];
  const dealScore = results?.deal_score;
  const hasData = Object.keys(results || {}).length > 0;

  const positiveFlags = flags.filter(f => f.type === 'positive');
  const negativeFlags = flags.filter(f => f.type === 'negative');

  const handleDelete = useCallback(async (id, e) => {
    e.stopPropagation();
    try {
      await analysesApi.delete(id);
      toast.success('Analysis deleted');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to delete');
    }
  }, [onRefresh]);

  const handleDuplicate = useCallback(async (id, e) => {
    e.stopPropagation();
    try {
      const res = await analysesApi.duplicate(id);
      toast.success('Analysis duplicated');
      if (onSelect) onSelect(res.data.id);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to duplicate');
    }
  }, [onSelect, onRefresh]);

  const handleStatusChange = useCallback(async (id, newStatus, e) => {
    if (e) e.stopPropagation();
    try {
      await analysesApi.updateStatus(id, newStatus);
      toast.success(newStatus === 'complete' ? 'Analysis marked as complete' : 'Analysis reopened');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to update status');
    }
  }, [onRefresh]);

  const currentStatus = analysis?.status || 'draft';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {hasData ? (
        <>
          {/* Top actions bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-zinc-100 font-['Manrope']">{analysis?.name || 'Analysis'}</h2>
              <RenameInline analysisId={analysis?.id} currentName={analysis?.name} onRefresh={onRefresh} />
              <StatusBadge status={currentStatus} />
            </div>
            <div className="flex items-center gap-2">
              {currentStatus === 'complete' ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => handleStatusChange(analysis?.id, 'in_progress', e)}
                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  data-testid="reopen-analysis-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reopen
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => handleStatusChange(analysis?.id, 'complete', e)}
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid="mark-complete-btn"
                >
                  <Archive className="w-3.5 h-3.5 mr-1.5" /> Mark Complete
                </Button>
              )}
              <ShareButton analysisId={analysis?.id} />
              <Button variant="outline" size="sm" onClick={onNew} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="new-analysis-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New
              </Button>
            </div>
          </div>

          {/* Hero Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="Enterprise Value (DCF)"
              value={fmt(dcf.enterprise_value)}
              sub={`Implied ${dcf.implied_multiple?.toFixed(2) || '—'}x multiple`}
              icon={<TrendingUp className="w-4 h-4" />}
              accent="indigo"
            />
            <MetricCard
              label="Buyer DSCR"
              value={dc.buyer?.dscr?.toFixed(2) + 'x' || '—'}
              sub={dc.buyer?.dscr >= 1.35 ? 'Above SBA threshold' : 'Below SBA 1.35x'}
              icon={<Shield className="w-4 h-4" />}
              accent={dc.buyer?.dscr >= 1.35 ? 'emerald' : 'red'}
            />
            <MetricCard
              label="FCF to Buyer"
              value={fmt(dc.buyer?.fcf_to_buyer)}
              sub="Annual free cash flow"
              icon={<BarChart3 className="w-4 h-4" />}
              accent="emerald"
            />
            <MetricCard
              label="Trend Flags"
              value={`${positiveFlags.length} / ${negativeFlags.length}`}
              sub="Positive / Negative"
              icon={<Brain className="w-4 h-4" />}
              accent={negativeFlags.length > positiveFlags.length ? 'red' : 'emerald'}
            />
          </div>

          {/* Deal Score */}
          {dealScore && (
            <DealScoreCard 
              dealScore={dealScore} 
              analysisId={analysis?.id}
              onScoreUpdate={(newScore) => {
                if (onRefresh) onRefresh();
              }}
              showConfig={true}
            />
          )}

          {/* Deal Overview & Flags */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Deal Comparison Quick View</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {['seller', 'buyer', 'comparable'].map((key) => {
                    const d = dc[key];
                    if (!d) return null;
                    const labelMap = { seller: 'Seller', buyer: 'Buyer', comparable: 'Comparable' };
                    return (
                      <div key={key} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0">
                        <span className="text-xs text-zinc-400">{labelMap[key]}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-zinc-500">Total Deal</span>
                          <span className="font-mono text-sm text-zinc-200">{fmt(d.total_deal_size)}</span>
                          <span className="text-xs text-zinc-500">DSCR</span>
                          <Badge variant="outline" className={d.dscr >= 1.35 ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}>
                            {d.dscr?.toFixed(2)}x
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-3 text-indigo-400 hover:text-indigo-300" onClick={() => onNavigate('deal-comparison')} data-testid="goto-deal-comparison">
                  View Full Comparison <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Key Trend Flags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {flags.slice(0, 6).map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs py-1.5">
                      <span className={`shrink-0 w-1.5 h-1.5 rounded-full mt-1 ${f.type === 'positive' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                      <span className="text-zinc-500 font-mono w-16 shrink-0">{f.category}</span>
                      <span className="text-zinc-300">{f.message}</span>
                    </div>
                  ))}
                  {flags.length === 0 && <p className="text-xs text-zinc-500">Import data to see trend flags</p>}
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-3 text-indigo-400 hover:text-indigo-300" onClick={() => onNavigate('findings')} data-testid="goto-findings">
                  View AI Analysis <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Scenario Summary */}
          {results?.scenarios && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Scenario Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(results.scenarios).map(([name, s]) => (
                    <div key={name} className="p-3 rounded-lg border border-zinc-800/50 bg-zinc-900/40">
                      <div className="text-xs text-zinc-400 mb-1">{name}</div>
                      <div className="font-mono text-lg text-zinc-100">{fmt(s.enterprise_value)}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        vs Asking: <span className={s.enterprise_value > (dc.seller?.purchase_price || 0) ? 'text-emerald-400' : 'text-red-400'}>
                          {fmt(s.enterprise_value - (dc.seller?.purchase_price || 0))}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Other Analyses */}
          {analyses.length > 1 && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">All Analyses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {analyses.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => onSelect(a.id)}
                      className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        a.id === analysis?.id ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-zinc-800/50 bg-zinc-900/40 hover:border-zinc-700'
                      }`}
                      data-testid={`analysis-item-${a.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-zinc-200">{a.name}</span>
                            <StatusBadge status={a.status} />
                          </div>
                          <div className="text-[10px] text-zinc-500 font-mono">{new Date(a.updated_at || a.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {a.status === 'complete' ? (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" onClick={(e) => handleStatusChange(a.id, 'in_progress', e)} data-testid={`reopen-${a.id}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" onClick={(e) => handleStatusChange(a.id, 'complete', e)} data-testid={`complete-${a.id}`}>
                            <Archive className="w-3 h-3 mr-1" /> Complete
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-300" onClick={(e) => handleDuplicate(a.id, e)} data-testid={`dup-${a.id}`}>
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400" onClick={(e) => handleDelete(a.id, e)} data-testid={`del-${a.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-6">
            <BarChart3 className="w-8 h-8 text-indigo-400" />
          </div>
          <h2 className="text-2xl font-semibold text-zinc-200 font-['Manrope'] mb-2">Welcome to AcquiFlow</h2>
          <p className="text-sm text-zinc-400 max-w-md mb-6">
            Upload a financial analysis spreadsheet or create a new analysis to get started with your business acquisition due diligence.
          </p>
          <div className="flex gap-3">
            <Button onClick={onNew} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="create-analysis-btn">
              <Plus className="w-4 h-4 mr-2" /> New Analysis
            </Button>
            <Button variant="outline" onClick={() => onNavigate('import')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="import-data-btn">
              Import Spreadsheet
            </Button>
          </div>

          {analyses.length > 0 && (
            <div className="mt-8 w-full max-w-lg">
              <h3 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Recent Analyses</h3>
              {analyses.slice(0, 5).map((a) => (
                <div key={a.id} className="flex items-center justify-between p-3 rounded-lg border border-zinc-800/50 bg-zinc-900/40 hover:border-indigo-500/30 transition-colors mb-2 cursor-pointer" onClick={() => onSelect(a.id)} data-testid={`analysis-${a.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-zinc-200">{a.name}</span>
                      <StatusBadge status={a.status} />
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">{new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-300" onClick={(e) => handleDuplicate(a.id, e)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400" onClick={(e) => handleDelete(a.id, e)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RenameInline({ analysisId, currentName, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(currentName || '');

  const save = async () => {
    if (!name.trim() || !analysisId) return;
    try {
      await analysesApi.rename(analysisId, name.trim());
      setEditing(false);
      if (onRefresh) onRefresh();
      toast.success('Renamed');
    } catch (e) {
      toast.error('Failed to rename');
    }
  };

  if (!editing) {
    return (
      <Button variant="ghost" size="sm" onClick={() => { setName(currentName || ''); setEditing(true); }} className="h-6 w-6 p-0 text-zinc-500 hover:text-zinc-300" data-testid="rename-btn">
        <Pencil className="w-3 h-3" />
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        autoFocus
        className="bg-zinc-800 border border-zinc-600 rounded text-sm text-zinc-200 px-2 py-0.5 w-48 focus:outline-none focus:border-indigo-500/50"
        data-testid="rename-input"
      />
      <Button variant="ghost" size="sm" onClick={save} className="h-6 w-6 p-0 text-emerald-400" data-testid="rename-save">
        <Check className="w-3 h-3" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="h-6 w-6 p-0 text-zinc-500">
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}

function MetricCard({ label, value, sub, icon, accent = 'indigo' }) {
  const colors = {
    indigo: 'border-indigo-500/20 bg-indigo-500/5',
    emerald: 'border-emerald-500/20 bg-emerald-500/5',
    red: 'border-red-500/20 bg-red-500/5',
    amber: 'border-amber-500/20 bg-amber-500/5',
  };
  const iconColors = {
    indigo: 'text-indigo-400',
    emerald: 'text-emerald-400',
    red: 'text-red-400',
    amber: 'text-amber-400',
  };

  return (
    <div className={`p-4 rounded-lg border ${colors[accent]} transition-all duration-200 hover:scale-[1.02]`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={iconColors[accent]}>{icon}</span>
        <span className="text-xs text-zinc-400">{label}</span>
      </div>
      <div className="font-mono text-xl font-semibold text-zinc-100">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}
